import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Plus, Link2, MousePointerClick, BarChart3, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DashboardLinksTable,
  LShortenForm,
  BlinkingCursor,
} from '@/components/l'
import { useAuth } from '@/hooks/use-auth'
import { listLinksFn, getDashboardStatsFn } from '@/server/functions/links'
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

export const Route = createFileRoute('/_protected/dashboard')({
  loader: async () => {
    const [linksResult, statsResult] = await Promise.all([
      listLinksFn(),
      getDashboardStatsFn(),
    ])
    return { links: linksResult.links, stats: statsResult }
  },
  component: DashboardPage,
})

function DashboardPage() {
  const { links, stats } = Route.useLoaderData()
  const { currentUser, signOut } = useAuth()
  const navigate = useNavigate()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    navigate({ to: '/' })
  }

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-bold text-white font-mono"
            >
              DASHBOARD
              <BlinkingCursor className="text-[#9B87F5] ml-2" />
            </motion.h1>
            <p className="text-[#8E9196] font-mono text-sm mt-1">
              // Welcome back, {currentUser?.email?.split('@')[0]}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#9B87F5] hover:bg-[#8B5CF6] text-black font-mono font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  NEW_LINK
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1A1F2C] border-[#9B87F5]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white font-mono">
                    CREATE_NEW_LINK
                  </DialogTitle>
                </DialogHeader>
                <LShortenForm onSuccess={() => setCreateDialogOpen(false)} />
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="border-[#9B87F5]/30 text-[#8E9196] hover:text-white hover:bg-[#9B87F5]/10 font-mono"
            >
              <LogOut className="w-4 h-4 mr-2" />
              SIGN_OUT
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <StatCard
            icon={<Link2 className="w-5 h-5" />}
            value={stats.totalLinks}
            label="TOTAL_LINKS"
          />
          <StatCard
            icon={<MousePointerClick className="w-5 h-5" />}
            value={stats.totalClicks}
            label="TOTAL_CLICKS"
          />
          <StatCard
            icon={<BarChart3 className="w-5 h-5" />}
            value={stats.clicksToday}
            label="CLICKS_TODAY"
          />
          <StatCard
            icon={<BarChart3 className="w-5 h-5" />}
            value={stats.clicksThisWeek}
            label="CLICKS_WEEK"
          />
        </motion.div>

        {/* Links Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1A1F2C]/50 border border-[#9B87F5]/10 rounded-lg overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-[#9B87F5]/10">
            <h2 className="text-lg font-bold text-white font-mono">
              YOUR_LINKS
            </h2>
          </div>
          <DashboardLinksTable links={links} />
        </motion.div>
      </div>
    </main>
  )
}

function StatCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode
  value: number
  label: string
}) {
  return (
    <div className="bg-[#1A1F2C]/50 border border-[#9B87F5]/10 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-[#9B87F5]/10 rounded text-[#9B87F5]">{icon}</div>
      </div>
      <div className="text-2xl font-bold text-white font-mono">{value}</div>
      <div className="text-xs text-[#8E9196] font-mono">{label}</div>
    </div>
  )
}
